import React, {useEffect, useState} from "react";
import {
  AppBar as MUAppBar,
  Box,
  ButtonBase,
  ThemeProvider,
  Toolbar,
  useTheme,
} from "@material-ui/core";
import BackIcon from "@material-ui/icons/KeyboardBackspace";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import {useSelector} from "react-redux";
import {Link, useNavigate} from "react-router-dom";
import {selectUser} from "store/auth/selectors";
import {selectMetadata} from "store/metadata/selectors";
import {
  useAdmin,
  useCurrentShowId,
  useExhibitor,
  useIsShowLevelOrganiser,
  useOrganiser,
  useSupplier,
} from "hooks";
import Tab from "view/components/Tab";
import Tabs from "view/components/Tabs";
import {FFairPrimaryIconBlueLogo} from "view/images/FFairLogo";
import MaintenanceModePanel from "view/components/AppBar/components/MaintenanceModePanel";
import useBranding from "store/branding/branding.store";
import {useGenerateThemeFromBranding} from "view/apps/Exhibitor/helpers";
import useViewMode, {ViewMode} from "store/viewMode/viewMode.store";
import TruncatedText from "view/components/TruncatedText";
import {HeaderType, useHeaderView} from "store/header/header.store";
import {Roles} from "@evennt-tech/api-v2-core";
import SettingsButton from "view/components/SettingsButton";
import useLocalizedLocalMap from "hooks/useLocalizedLocalMap";
import useCurrentSupplierCompanyId from "hooks/useCurrentSupplierCompanyId";
import useStyles from "./styles";
import UserActions from "./components/UserActions";
import UserFullName from "./components/UserFullName";
import ShowBar from "./components/ShowBar";
import Avatar from "../Avatar";
import Notification from "./components/NotificationsPopup";
import SupplierNotification from "./components/NotificationsPopup/SupplierNotification";
import FlexBox from "../Layout/FlexBox";
import QuickOrderHeader from "./components/QuickOrderHeader";
import ImpersonationBanner from "./components/ImpersonationBanner";

export const APP_BAR_HEIGHT = 64;

const AppBar = () => {
  const history = useNavigate();

  const showId = useCurrentShowId();
  const [organiser] = useOrganiser();
  const [supplier] = useSupplier();
  const [admin] = useAdmin();
  const exhibitor = useExhibitor();

  const {headerView} = useHeaderView();

  const {mode} = useViewMode();
  const supplierCompanyId = useCurrentSupplierCompanyId();

  const {getBranding} = useBranding();
  const branding = getBranding(showId);

  const [fullName, setFullName] = useState<string>("");
  const {role} = useSelector(selectUser);
  const isShowLevelOrganiser = useIsShowLevelOrganiser();
  const {hideSidebar, backPath, appBarBottomContent, showBar} = useSelector(
    selectMetadata
  );
  const classes = useStyles({
    variant: appBarBottomContent?.variant,
  });
  const theme = useTheme();
  const exhibitorShowTheme = useGenerateThemeFromBranding(branding);
  const {exhibitor: localizedExhibitorLabel} = useLocalizedLocalMap();

  useEffect(() => {
    switch (role) {
      case "organiser": {
        if (!organiser?.contactPerson) {
          return;
        }
        setFullName(organiser?.contactPerson);
        return;
      }
      case "supplier": {
        if (!supplier) {
          return;
        }
        const supplierName = `${supplier?.firstName} ${supplier?.lastName}`.trim();
        setFullName(supplierName);
        return;
      }
      case "admin": {
        if (!admin) {
          return;
        }
        const adminName = `${admin?.firstName} ${admin?.lastName}`.trim();
        setFullName(adminName);
        return;
      }
      case "exhibitor": {
        if (!exhibitor) {
          return;
        }
        setFullName(exhibitor?.name);
        return;
      }
      default:
        setFullName("");
    }
  }, [supplier, admin, organiser, exhibitor, role, setFullName]);

  const renderBottomContent = () => {
    if (hideSidebar) {
      return (
        <Box className={classes.bottomContentContainer}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            {(appBarBottomContent?.lastPage ||
              appBarBottomContent?.mainText) && (
              <Box display="flex">
                <ButtonBase
                  onClick={() => {
                    if (backPath) {
                      history(backPath);
                      return;
                    }
                    history(-1);
                  }}
                >
                  <BackIcon className={classes.backIcon} />
                </ButtonBase>
                <Box className={classes.bottomContentText}>
                  <p>{appBarBottomContent?.lastPage}</p>

                  <TruncatedText
                    variant="h2"
                    text={appBarBottomContent?.mainText || ""}
                    className={classes.mainText}
                    maxLength={100}
                  />
                </Box>
              </Box>
            )}
            {appBarBottomContent?.showOptions ? (
              <ButtonBase>
                <MoreVertIcon className={classes.moreIcon} />
              </ButtonBase>
            ) : null}
          </Box>
          {appBarBottomContent.tabs && (
            <Box>
              <Tabs
                updateIndicatorAfterRender
                value={appBarBottomContent.tabs.value}
                onChange={appBarBottomContent.tabs.onChange}
              >
                {appBarBottomContent.tabs.options.map(({label}, idx) => (
                  <Tab key={idx} label={label} style={{minWidth: "90px"}} />
                ))}
              </Tabs>
            </Box>
          )}
        </Box>
      );
    }
    return null;
  };

  const shouldShowShowBar =
    (showId && role && [Roles.SUPPLIER, Roles.EXHIBITOR].includes(role)) ||
    mode === ViewMode.EXHIBITOR;

  if (shouldShowShowBar) {
    const content = {
      [HeaderType.STANDARD]: (
        <ShowBar fullName={fullName} role={role as Roles} {...showBar} />
      ),
      [HeaderType.QUICK_ORDER]: <QuickOrderHeader />,
    }[headerView.type];

    const shouldApplyExhibitorTheme =
      mode === ViewMode.EXHIBITOR || role === Roles.EXHIBITOR;
    return shouldApplyExhibitorTheme ? (
      <ThemeProvider theme={exhibitorShowTheme || theme}>
        {content}
      </ThemeProvider>
    ) : (
      content
    );
  }

  const isExhibitorView =
    role === Roles.EXHIBITOR || mode === ViewMode.EXHIBITOR;

  return (
    <>
      <MUAppBar position="fixed" className={classes.appBar}>
        <ImpersonationBanner fullName={fullName} />
        <MaintenanceModePanel />
        <Toolbar className={classes.toolbar} style={{height: APP_BAR_HEIGHT}}>
          <Box className={classes.sideLeft}>
            <Link to="/">
              <FFairPrimaryIconBlueLogo />
            </Link>
            <Box className={classes.sideText}>
              <Box className={classes.topLeftSideText}>FFAIR</Box>
              <Box className={classes.botLeftSideText} id="cy-console-role">
                {isExhibitorView
                  ? `${localizedExhibitorLabel} Console`
                  : `${role} Console`}
              </Box>
            </Box>
          </Box>
          <FlexBox alignItems="center" m={[1.5, 0]}>
            {organiser?.id && <Notification organiserId={organiser.id} />}
            {role === Roles.SUPPLIER && supplierCompanyId && (
              <SupplierNotification supplierCompanyId={supplierCompanyId} />
            )}
            <Box className={classes.sideText}>
              <UserFullName fullName={fullName} />
              <Box className={classes.botRightSideText}>
                {isExhibitorView ? localizedExhibitorLabel : role}
              </Box>
            </Box>
            <Avatar
              name={fullName}
              alt={fullName}
              showFallback
              className={classes.avatar}
            />
            {role === Roles.EXHIBITOR && <SettingsButton />}
            {isShowLevelOrganiser && <SettingsButton />}
            <UserActions />
          </FlexBox>
        </Toolbar>
        {renderBottomContent()}
      </MUAppBar>
    </>
  );
};

export default AppBar;
